// Product Modal Component
// Modal for creating new products
// Three-column layout: Name/Description | Tags/Price | Image Preview

import React, { useState } from 'react';
import { RevoltModal } from '../../../../components/RevoltModal';
import { RevoltInput } from '../../../../components/RevoltInput';
import { RevoltTextarea } from '../../../../components/RevoltTextarea';
import { RevoltButton } from '../../../../components/RevoltButton';
import { RevoltSwitch } from '../../../../components/RevoltSwitch';
import DropdownSelect from '../../../../components/DropdownSelect';
import './ProductModal.css';

export function ProductModal({
  isOpen,
  onClose,
  onSubmit
}) {
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [accountTags, setAccountTags] = useState([]);
  const [newAccountTag, setNewAccountTag] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [isBundle, setIsBundle] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [downloads, setDownloads] = useState([]);
  const [downloadMode, setDownloadMode] = useState('legacy'); // legacy | revolt-desktop
  const [newDownloadUrl, setNewDownloadUrl] = useState('');
  const [newDownloadName, setNewDownloadName] = useState('');
  const [newDownloadChannel, setNewDownloadChannel] = useState('main');
  const [newDownloadFileName, setNewDownloadFileName] = useState('');
  const [newDownloadFileSize, setNewDownloadFileSize] = useState('');
  const [newShowVersionBadge, setNewShowVersionBadge] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setPrice('');
      setTags([]);
      setNewTag('');
      setAccountTags([]);
      setNewAccountTag('');
      setIsFeatured(false);
      setIsBundle(false);
      setThumbnailUrl('');
      setDownloads([]);
      setDownloadMode('legacy');
      setNewDownloadUrl('');
      setNewDownloadName('');
      setNewDownloadChannel('main');
      setNewDownloadFileName('');
      setNewDownloadFileSize('');
      setNewShowVersionBadge(true);
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setErrorMessage('Product name is required');
      return;
    }

    if (!description.trim()) {
      setErrorMessage('Product description is required');
      return;
    }

    if (!price || isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      setErrorMessage('Please enter a valid price');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const productData = {
        name: name.trim(),
        description: description.trim(),
        price: parseFloat(price),
        tags: [...tags],
        accountTags: [...accountTags],
        thumbnail: thumbnailUrl.trim() || undefined,
        downloads: downloads.map((d) => {
          if (d && typeof d === 'object' && d.mode === 'revolt-desktop') {
            return {
              mode: 'revolt-desktop',
              channel: d.channel,
              name: d.name,
              showVersionBadge: Boolean(d.showVersionBadge),
            };
          }
          return {
            url: d.url,
            name: d.name,
            fileName: d.fileName || '',
            fileSize: d.fileSize || ''
          };
        }),
        isFeatured,
        isBundle,
        isActive: true
      };

      // Call the backend API to create the product
      const response = await fetch('/api/admin/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create product');
      }

      const result = await response.json();

      // Call the onSubmit callback if provided
      if (onSubmit) {
        await onSubmit(result.product);
      }

      // Success - close modal
      onClose();
    } catch (error) {
      console.error('Error creating product:', error);
      setErrorMessage(error.message || 'Failed to create product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTag = () => {
    const tagValue = newTag.trim().toLowerCase();
    if (tagValue && !tags.includes(tagValue)) {
      setTags([...tags, tagValue]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleAddAccountTag = () => {
    const accountTagValue = newAccountTag.trim().toLowerCase();
    if (accountTagValue && !accountTags.includes(accountTagValue)) {
      setAccountTags([...accountTags, accountTagValue]);
      setNewAccountTag('');
    }
  };

  const handleRemoveAccountTag = (accountTagToRemove) => {
    setAccountTags(accountTags.filter(tag => tag !== accountTagToRemove));
  };

  const handleAccountTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAccountTag();
    }
  };

  const handleAddDownload = () => {
    const name = newDownloadName.trim();

    if (downloadMode === 'revolt-desktop') {
      const channel = (newDownloadChannel || '').trim().toLowerCase();
      if (!name || !channel) return;

      setDownloads([
        ...downloads,
        {
          mode: 'revolt-desktop',
          channel,
          name,
          showVersionBadge: Boolean(newShowVersionBadge),
        },
      ]);

      setNewDownloadName('');
      setNewDownloadChannel('main');
      setNewShowVersionBadge(true);
      return;
    }

    const url = newDownloadUrl.trim();
    if (!url || !name) return;

    setDownloads([
      ...downloads,
      { url, name, fileName: newDownloadFileName.trim(), fileSize: newDownloadFileSize.trim() },
    ]);
    setNewDownloadUrl('');
    setNewDownloadName('');
    setNewDownloadFileName('');
    setNewDownloadFileSize('');
  };

  const handleRemoveDownload = (indexToRemove) => {
    setDownloads(downloads.filter((_, i) => i !== indexToRemove));
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Product"
      containerClassName="product-modal-container modal-wide"
    >
      <div className="modal-scrollable-content">
        {errorMessage && (
          <div className="modal-error-message">
            <i className="fa-solid fa-exclamation-triangle"></i>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="product-modal-form">
          <div className="product-modal-columns">
            {/* Left Column - Name & Description */}
            <div className="product-modal-left-column">
              <div className="form-group">
                <label htmlFor="product-name" className="form-label">
                  Product Name <span className="required">*</span>
                </label>
                <RevoltInput
                  id="product-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter product name..."
                  required
                  maxLength={100}
                />
                <div className="form-help">
                  A clear, descriptive name for your product.
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="product-description" className="form-label">
                  Description <span className="required">*</span>
                </label>
                <RevoltTextarea
                  id="product-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your product..."
                  rows={6}
                  required
                />
                <div className="form-help">
                  A detailed description of what your product offers.
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="product-price" className="form-label">
                  Price (USD) <span className="required">*</span>
                </label>
                <RevoltInput
                  id="product-price"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  required
                />
                <div className="form-help">
                  Set the price for your product in USD.
                </div>
              </div>

              <div className="form-group">
                <div className="featured-switch-container">
                  <span className="featured-label">Featured Product</span>
                  <RevoltSwitch
                    checked={isFeatured}
                    onChange={setIsFeatured}
                    className="featured-switch"
                  />
                </div>
                <div className="form-help">
                  Featured products appear prominently when users visit the products page without searching.
                </div>
              </div>

              <div className="form-group">
                <div className="featured-switch-container">
                  <span className="featured-label">Product Bundle</span>
                  <RevoltSwitch
                    checked={isBundle}
                    onChange={setIsBundle}
                    className="featured-switch"
                  />
                </div>
                <div className="form-help">
                  Bundles display differently in the product grid and can represent a cluster of products.
                </div>
              </div>
            </div>

            {/* Middle Column - Tags */}
            <div className="product-modal-middle-column">
              <div className="form-group">
                <label className="form-label">Tags</label>

                {/* Current Tags */}
                {tags.length > 0 && (
                  <div className="tags-list">
                    {tags.map((tag, index) => (
                      <span key={index} className="tag-item">
                        {tag}
                        <button
                          type="button"
                          className="tag-remove"
                          onClick={() => handleRemoveTag(tag)}
                          aria-label={`Remove ${tag} tag`}
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add New Tag */}
                <div className="add-tag-section">
                  <RevoltInput
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                    placeholder="Add a tag..."
                    maxLength={30}
                  />
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={handleAddTag}
                    disabled={!newTag.trim() || tags.includes(newTag.trim().toLowerCase())}
                  >
                    <i className="fa-solid fa-plus"></i>
                    Add
                  </RevoltButton>
                </div>
                <div className="form-help">
                  Tags help users find your product. Press Enter or click Add to add tags.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Account Access Tags</label>

                {/* Current Account Tags */}
                {accountTags.length > 0 && (
                  <div className="tags-list">
                    {accountTags.map((tag, index) => (
                      <span key={index} className="tag-item">
                        {tag}
                        <button
                          type="button"
                          className="tag-remove"
                          onClick={() => handleRemoveAccountTag(tag)}
                          aria-label={`Remove ${tag} account tag`}
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add New Account Tag */}
                <div className="add-tag-section">
                  <RevoltInput
                    type="text"
                    value={newAccountTag}
                    onChange={(e) => setNewAccountTag(e.target.value)}
                    onKeyPress={handleAccountTagKeyPress}
                    placeholder="Add account access tag..."
                    maxLength={50}
                  />
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={handleAddAccountTag}
                    disabled={!newAccountTag.trim() || accountTags.includes(newAccountTag.trim().toLowerCase())}
                  >
                    <i className="fa-solid fa-plus"></i>
                    Add
                  </RevoltButton>
                </div>
                <div className="form-help">
                  These tags are added to user accounts when they purchase this product. Use for product bundles/packages.
                </div>
              </div>

              {/* Downloadable Content */}
              <div className="form-group">
                <label className="form-label">Downloadable Content</label>
                {downloads.length > 0 && (
                  <div className="product-modal-downloads-list">
                    {downloads.map((d, index) => (
                      <div key={index} className="product-modal-download-item">
                        <span className="product-modal-download-name">{d.name}</span>
                        {d.mode === 'revolt-desktop' ? (
                          <span className="product-modal-download-size">{d.channel}</span>
                        ) : (
                          d.fileSize && <span className="product-modal-download-size">{d.fileSize}</span>
                        )}
                        <button
                          type="button"
                          className="tag-remove"
                          onClick={() => handleRemoveDownload(index)}
                          aria-label={`Remove ${d.name}`}
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="product-modal-add-download">
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                      Type
                    </label>
                    <div style={{ marginTop: 6 }}>
                      <DropdownSelect
                        id="product-modal-download-type"
                        value={downloadMode}
                        options={[
                          { value: 'legacy', label: 'Static download link' },
                          { value: 'revolt-desktop', label: 'Revolt Desktop (updater channel)' },
                        ]}
                        onValueChange={(v) => setDownloadMode(v)}
                      />
                    </div>
                  </div>

                  <RevoltInput
                    type="text"
                    value={newDownloadName}
                    onChange={(e) => setNewDownloadName(e.target.value)}
                    placeholder="Display name"
                  />
                  {downloadMode === 'revolt-desktop' ? (
                    <>
                      <RevoltInput
                        type="text"
                        value={newDownloadChannel}
                        onChange={(e) => setNewDownloadChannel(e.target.value)}
                        placeholder="main / beta / dev"
                      />
                      <RevoltSwitch
                        id="product-modal-download-show-version-badge"
                        checked={Boolean(newShowVersionBadge)}
                        onChange={setNewShowVersionBadge}
                        label="Show version badge"
                      />
                    </>
                  ) : (
                    <>
                      <RevoltInput
                        type="text"
                        value={newDownloadFileName}
                        onChange={(e) => setNewDownloadFileName(e.target.value)}
                        placeholder="File name (e.g. setup.exe)"
                      />
                      <RevoltInput
                        type="url"
                        value={newDownloadUrl}
                        onChange={(e) => setNewDownloadUrl(e.target.value)}
                        placeholder="https://..."
                      />
                      <RevoltInput
                        type="text"
                        value={newDownloadFileSize}
                        onChange={(e) => setNewDownloadFileSize(e.target.value)}
                        placeholder="e.g. 25 MB"
                      />
                    </>
                  )}
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={handleAddDownload}
                    disabled={
                      downloadMode === 'revolt-desktop'
                        ? !newDownloadChannel.trim() || !newDownloadName.trim()
                        : !newDownloadUrl.trim() || !newDownloadName.trim()
                    }
                  >
                    <i className="fa-solid fa-plus"></i>
                    Add
                  </RevoltButton>
                </div>
                <div className="form-help">
                  Optional. Add legacy downloads (static URL) or Revolt Desktop updater targets (channel + optional version badge).
                </div>
              </div>
            </div>

            {/* Right Column - Thumbnail */}
            <div className="product-modal-right-column">
              <div className="form-group">
                <label htmlFor="product-thumbnail" className="form-label">
                  Product Thumbnail
                </label>
                <RevoltInput
                  id="product-thumbnail"
                  type="url"
                  value={thumbnailUrl}
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
                <div className="form-help">
                  Enter a URL for the product thumbnail. Leave empty to use a default image.
                </div>
              </div>

              {/* Thumbnail Preview */}
              <div className="form-group">
                <label className="form-label">Thumbnail Preview</label>
                <div className="image-preview-container">
                  {thumbnailUrl ? (
                    <div className="image-preview-wrapper">
                      <img
                        src={thumbnailUrl}
                        alt="Product preview"
                        className="image-preview"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x300/333/666?text=Invalid+Image+URL';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="image-preview-placeholder">
                      <i className="fa-solid fa-image"></i>
                      <span>Enter a thumbnail URL above to see the preview</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="product-modal-footer">
        <div className="modal-actions">
          <RevoltButton
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </RevoltButton>

          <RevoltButton
            variant="primary"
            disabled={!name.trim() || !description.trim() || !price || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Creating...
              </>
            ) : (
              <>
                <i className="fa-solid fa-plus"></i>
                Create Product
              </>
            )}
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}